import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ClinicSettingsPanel } from "@/components/ClinicSettingsPanel";
import { Loader2, ChevronLeft, Users, ClipboardCheck, FileText, Save, Mail, Phone, MapPin } from "lucide-react";

const SUB_STATUSES = ["trial", "active", "past_due", "canceled"];
const PLANS = ["free", "starter", "professional", "enterprise"];

function InfoRow({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <Icon className="h-4 w-4 text-slate-400" /> <span>{children || "—"}</span>
    </div>
  );
}

export default function SuperAdminClinic() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");

  const load = async () => {
    try {
      const res = await api.get(`/super/clinics/${clinicId}`);
      setClinic(res.data);
      setStatus(res.data.subscription_status);
      setPlan(res.data.subscription_plan);
    } catch (e) {
      toast.error("Could not load Nimbus.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clinicId]);

  const saveSubscription = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/super/clinics/${clinicId}`, { subscription_status: status, subscription_plan: plan });
      setClinic(res.data);
      toast.success("Subscription updated.");
    } catch (e) {
      toast.error("Could not update subscription.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !clinic) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteHeader />
        <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" /></div>
      </div>
    );
  }

  const s = clinic.stats || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Button variant="ghost" onClick={() => navigate("/super")} className="mb-4" data-testid="back-to-super">
          <ChevronLeft className="mr-1 h-4 w-4" /> Back to Nimbus accounts
        </Button>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="clinic-detail-name">{clinic.name}</h1>
          <p className="mt-1 text-sm text-slate-500">{clinic.tagline}</p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <InfoRow icon={Mail}>{clinic.contact_email}</InfoRow>
            <InfoRow icon={Phone}>{clinic.phone}</InfoRow>
            <InfoRow icon={MapPin}>{clinic.address}</InfoRow>
            <InfoRow icon={Users}>Admin: {clinic.admin_email}</InfoRow>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slate-400"><Users className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Patients</span></div>
            <p className="mt-1 text-3xl font-bold text-slate-900" data-testid="detail-patients">{s.patients ?? 0}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slate-400"><ClipboardCheck className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Submitted</span></div>
            <p className="mt-1 text-3xl font-bold text-green-600" data-testid="detail-submitted">{s.submitted ?? 0}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slate-400"><FileText className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Form steps</span></div>
            <p className="mt-1 text-3xl font-bold text-[#0F52BA]" data-testid="detail-steps">{(clinic.template_steps || []).length}</p>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-semibold text-slate-900">Subscription & billing</h2>
          <p className="mt-1 text-sm text-slate-500">Billing collection is coming soon. For now, set the Nimbus account's plan and status manually.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="sub-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SUB_STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Plan</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger data-testid="sub-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLANS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button className="mt-4 bg-[#0F52BA] hover:bg-[#0B3D8A]" onClick={saveSubscription} disabled={saving} data-testid="save-subscription-btn">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes
          </Button>
        </div>

        <div className="mt-6">
          <h2 className="mb-3 font-semibold text-slate-900">Branding & paperwork</h2>
          <ClinicSettingsPanel clinicId={clinicId} />
        </div>
      </div>
    </div>
  );
}
